import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import SelectField from "../common/SelectField.js"
import { mlSheetService } from "../../services/mlSheetService"
import { shipmentService } from "../../services/shipmentService"
import companySettingsService from "../../services/companySettingsService.js"
import { paymentService } from "../../services/ShipmentPaymentService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from "../common/page_component/index.js"
import Loader from "../common/Loader"
import Alert from "../common/Alert"
import Button from "../common/Button.js"
import ListPagination from "../common/ListPagination.js"
import SearchSubmitInput from "../common/SearchSubmitInput.js"
import { mergeLoadedPage } from "../../utils/mergeLoadedPage.js"
import { ArticleNames, QuantityValue, ReceiverBranchValue, SenderBranchValue } from "./StatusShipmentFacts.js"
import PaymentQrModal from "../common/PaymentQrModal.js"
import DeliveryOtpModal from "../common/DeliveryOtpModal.js"
import { Package, RefreshCw, Undo2, Ban } from "lucide-react"
import { shipmentMatchesSearch } from "../../utils/mlSheetShipmentSearch.js"
import { printShipmentReceipts } from "../../utils/printShipmentReceipt.js"
import { getBulkStatusOptions, isCreditClientShipment, requiresDeliveryCollection } from "../../utils/shipmentPayment.js"
import { canRevertDelivery, revertDeliveryDisabledReason } from "../../utils/deliveryRevert.js"
import { isOtpRequiredResponse, normalizeOtpPending } from "../../utils/deliveryOtp.js"
import { useTranslation } from "react-i18next"
import { DEFAULT_LABOUR_CHARGE_POLICY, normalizeLabourChargePolicy, showLabourOnStatus } from "../../utils/labourChargePolicy.js"
import usePaginatedListFetch from "../../utils/usePaginatedListFetch.js"
import CapturePhotoField from "../common/CapturePhotoField.js"

const isStatusUpdateLocked = (status) => String(status || "").toLowerCase() === "delivered"
const FILTER_OPTIONS = ["Dispatched", "Received", "Delivered"]
const PAGE_SIZE = 10
const STATUS_SUBMIT_SELECTOR = '[data-agent-status-submit="true"]'

const focusVisibleSubmit = () => {
  const buttons = document.querySelectorAll(STATUS_SUBMIT_SELECTOR)
  const visible = Array.from(buttons).find((el) => el.offsetParent !== null)
  const target = visible || buttons[0]
  if (target && !target.disabled) target.focus()
}

const onEnter = (next) => (event) => {
  if (event.key !== "Enter") return
  event.preventDefault()
  if (typeof next === "function") next()
  else next?.current?.focus()
}

const isAgentOwnedHub = (shipment) =>
  String(shipment?.receiver_location_hub?.ownership_type || "").toUpperCase() === "AGENT"

const AgentUpdateStatus = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()

  const [shipments, setShipments] = useState([])
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [selectedMap, setSelectedMap] = useState(() => new Map())
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilters, setStatusFilters] = useState(["Received", "Dispatched"])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [selectableTotal, setSelectableTotal] = useState(0)
  const [allowDelivery, setAllowDelivery] = useState(
    user?.location_hub_allow_delivery !== false,
  )

  const [selectedStatus, setSelectedStatus] = useState("Received")
  const [remarks, setRemarks] = useState("Status updated via Update Status")

  const [deliveryReceiver, setDeliveryReceiver] = useState("")
  const [deliveryReceiverContact, setDeliveryReceiverContact] = useState("")
  const [deliveryReceiverImage, setDeliveryReceiverImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [deliveryGovIdImage, setDeliveryGovIdImage] = useState(null)
  const [govIdPreview, setGovIdPreview] = useState(null)
  const [unloadingCharges, setUnloadingCharges] = useState("")
  const [loadingCharges, setLoadingCharges] = useState("")
  const [deliveryVehicleNo, setDeliveryVehicleNo] = useState("")
  const [lastMileHours, setLastMileHours] = useState("2")
  const [labourPolicy, setLabourPolicy] = useState(DEFAULT_LABOUR_CHARGE_POLICY)
  const [chargeDefaults, setChargeDefaults] = useState({})

  const [paymentMode, setPaymentMode] = useState("upi")
  const [paymentAmount, setPaymentAmount] = useState("")
  const [showPaymentQr, setShowPaymentQr] = useState(false)
  const [paymentIntent, setPaymentIntent] = useState(null)
  const [pendingDeliverAfterPay, setPendingDeliverAfterPay] = useState(null)
  const [otpPending, setOtpPending] = useState([])
  const [revertingId, setRevertingId] = useState("")
  const deliveryNameRef = useRef(null)
  const deliveryContactRef = useRef(null)
  const paymentAmountRef = useRef(null)
  const paymentModeRef = useRef(null)
  const deliveryFocusKeyRef = useRef("")

  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
  )

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    const handler = (e) => setIsMobileLayout(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const selectId = currentPage === 1 ? searchParams.get("select") : ""
      const response = await mlSheetService.getAgentStatusShipments({
        page: currentPage,
        perPage: PAGE_SIZE,
        search: searchQuery,
        statuses: statusFilters,
        includeShipmentId: selectId || undefined,
      })
      const list = response.data?.shipments || []
      const pagination = response.data?.pagination || {}
      const pages = pagination.pages || 1
      const total = pagination.total ?? response.data?.total ?? list.length
      setShipments((prev) => mergeLoadedPage(currentPage, Array.isArray(list) ? list : [], prev))
      setTotalPages(pages)
      setTotalRecords(total)
      setSelectableTotal(
        pagination.selectable_total ??
          list.filter((s) => !isStatusUpdateLocked(s.status) && !s.is_deleted).length,
      )
      if (currentPage > pages) {
        setCurrentPage(pages)
      }
      if (typeof response.data?.location_hub_allow_delivery === "boolean") {
        setAllowDelivery(response.data.location_hub_allow_delivery)
      } else if (user?.location_hub_allow_delivery !== undefined) {
        setAllowDelivery(user.location_hub_allow_delivery !== false)
      }
      setSelectedMap((prev) => {
        if (prev.size === 0) return prev
        const next = new Map(prev)
        let changed = false
        list.forEach((shipment) => {
          if (!next.has(shipment.shipment_id)) return
          if (isStatusUpdateLocked(shipment.status) || shipment.is_deleted) {
            next.delete(shipment.shipment_id)
            changed = true
            return
          }
          next.set(shipment.shipment_id, shipment)
          changed = true
        })
        return changed ? next : prev
      })
    } catch (error) {
      console.error("Error fetching assigned shipments:", error)
      Alert.error(error.response?.data?.error || "Failed to load assigned shipments")
    } finally {
      setLoading(false)
    }
  }, [
    user?.location_hub_allow_delivery,
    currentPage,
    searchQuery,
    statusFilters,
    searchParams,
  ])

  usePaginatedListFetch({
    fetchPage: fetchData,
    setCurrentPage,
    resetPageDeps: [searchQuery, statusFilters.join("|")],
  })

  const selectedShipments = useMemo(
    () => new Set(selectedMap.keys()),
    [selectedMap],
  )

  const filteredShipments = shipments

  const selectableFilteredShipments = useMemo(
    () => filteredShipments.filter((s) => !isStatusUpdateLocked(s.status) && !s.is_deleted),
    [filteredShipments],
  )

  const selectedList = useMemo(
    () =>
      Array.from(selectedMap.values()).filter(
        (s) => s && !isStatusUpdateLocked(s.status) && !s.is_deleted,
      ),
    [selectedMap],
  )

  const unpaidSelected = useMemo(
    () => selectedList.filter((s) => requiresDeliveryCollection(s)),
    [selectedList],
  )

  const creditSelected = useMemo(
    () => selectedList.filter((s) => isCreditClientShipment(s)),
    [selectedList],
  )

  const agentHubSelected = useMemo(
    () => selectedList.filter((s) => isAgentOwnedHub(s)),
    [selectedList],
  )
  const showUnloadingInput = showLabourOnStatus(
    labourPolicy,
    'unloading',
    selectedStatus,
    {
      agentHub: agentHubSelected.length > 0,
      chargeEnabled: !!chargeDefaults.unloading?.enabled,
    },
  )
  const showLoadingInput = showLabourOnStatus(
    labourPolicy,
    'loading',
    selectedStatus,
    {
      agentHub: agentHubSelected.length > 0,
      chargeEnabled: !!chargeDefaults.loading?.enabled,
    },
  )

  useEffect(() => {
    companySettingsService.getSettings()
      .then((res) => {
        setLabourPolicy(normalizeLabourChargePolicy(res?.data?.labour_charge_policy))
        setChargeDefaults(res?.data?.shipment_charge_defaults || {})
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!showUnloadingInput) {
      setUnloadingCharges("")
      return
    }
    const first = agentHubSelected[0] || selectedList[0]
    const existing = Number(first?.unloading_charges || 0)
    setUnloadingCharges(existing > 0 ? String(existing) : "")
  }, [showUnloadingInput, agentHubSelected, selectedList])

  useEffect(() => {
    if (!showLoadingInput) {
      setLoadingCharges("")
      return
    }
    const existing = Number(selectedList[0]?.loading_charges || 0)
    setLoadingCharges(existing > 0 ? String(existing) : "")
  }, [showLoadingInput, selectedList])

  const needsDeliveryPayment =
    selectedStatus === "Delivered" && unpaidSelected.length > 0

  const bulkStatusConfig = useMemo(
    () => getBulkStatusOptions(new Set(selectedList.map((s) => s.shipment_id)), selectedList),
    [selectedList],
  )

  const deliveryBlocked = user?.role === "Agent" && !allowDelivery

  const statusOptions = useMemo(() => {
    const base =
      bulkStatusConfig.options.length > 0
        ? bulkStatusConfig.options
        : deliveryBlocked
          ? ["Received"]
          : ["Received", "Delivered"]
    let options = base.map((status) => ({ value: status, label: status }))
    if (deliveryBlocked) {
      options = options.filter((o) => o.value !== "Delivered")
    }
    return options
  }, [bulkStatusConfig.options, deliveryBlocked])

  useEffect(() => {
    const urlStatus = String(searchParams.get("status") || "").toLowerCase()
    if (urlStatus === "delivered") return
    const auto = bulkStatusConfig.autoSelect
    if (auto && (auto !== "Delivered" || !deliveryBlocked)) {
      setSelectedStatus(auto)
      return
    }
    setSelectedStatus((prev) => {
      const values = statusOptions.map((o) => o.value)
      if (values.includes(prev)) return prev
      return values[0] || "Received"
    })
  }, [bulkStatusConfig, statusOptions, deliveryBlocked, searchParams])

  useEffect(() => {
    const selectId = searchParams.get("select")
    if (!selectId || !shipments.length) return
    const match = shipments.find((s) => String(s.shipment_id) === String(selectId))
    if (match && !isStatusUpdateLocked(match.status) && !match.is_deleted) {
      setSelectedMap((prev) => {
        if (prev.has(match.shipment_id)) return prev
        const next = new Map(prev)
        next.set(match.shipment_id, match)
        return next
      })
    }
    if (String(searchParams.get("status") || "").toLowerCase() === "delivered" && !deliveryBlocked) {
      setSelectedStatus("Delivered")
    }
  }, [searchParams, shipments, deliveryBlocked])

  useEffect(() => {
    window.__jabbarStatusSelectedCount = selectedShipments.size
    const onSet = () => {
      if (selectedShipments.size > 0 && !deliveryBlocked) setSelectedStatus("Delivered")
    }
    window.addEventListener("jabbar-set-delivered", onSet)
    return () => {
      window.removeEventListener("jabbar-set-delivered", onSet)
      window.__jabbarStatusSelectedCount = 0
    }
  }, [selectedShipments, deliveryBlocked])

  useEffect(() => {
    setSelectedMap((prev) => {
      if (prev.size === 0) return prev
      let changed = false
      const next = new Map()
      prev.forEach((shipment, id) => {
        const statusOk = statusFilters.some(
          (f) => String(shipment.status || "").toLowerCase() === f.toLowerCase(),
        )
        const searchOk = shipmentMatchesSearch(shipment, searchQuery)
        if (statusOk && searchOk && !isStatusUpdateLocked(shipment.status) && !shipment.is_deleted) {
          next.set(id, shipment)
        } else {
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [statusFilters, searchQuery])

  useEffect(() => {
    if (needsDeliveryPayment && unpaidSelected.length === 1) {
      setPaymentAmount(String(parseFloat(unpaidSelected[0].balance_amount || 0).toFixed(2)))
    } else if (!needsDeliveryPayment) {
      setPaymentAmount("")
    }
  }, [needsDeliveryPayment, unpaidSelected])

  useEffect(() => {
    if (loading || selectedStatus !== "Delivered" || deliveryBlocked || selectedList.length === 0) {
      return undefined
    }
    const firstId = selectedList[0]?.shipment_id || ""
    const key = `${selectedStatus}:${firstId}:${selectedList.length}`
    if (deliveryFocusKeyRef.current === key) return undefined
    deliveryFocusKeyRef.current = key
    const timer = window.setTimeout(() => {
      deliveryNameRef.current?.focus()
      deliveryNameRef.current?.select?.()
    }, 80)
    return () => window.clearTimeout(timer)
  }, [loading, selectedStatus, deliveryBlocked, selectedList])

  const goToAmountOrSubmit = () => {
    if (needsDeliveryPayment && unpaidSelected.length === 1 && paymentModeRef.current) {
      paymentModeRef.current.focus()
      return
    }
    focusVisibleSubmit()
  }

  const allFilteredSelected =
    selectableFilteredShipments.length > 0 &&
    selectableFilteredShipments.every((s) => selectedShipments.has(s.shipment_id))

  const toggleStatusFilter = (status) => {
    setStatusFilters((prev) => {
      if (prev.includes(status)) {
        if (prev.length === 1) return prev
        return prev.filter((s) => s !== status)
      }
      return [...prev, status]
    })
  }

  const handleSelectAll = (e) => {
    const checked = e.target.checked
    setSelectedMap((prev) => {
      const next = new Map(prev)
      if (checked) {
        selectableFilteredShipments.forEach((s) => next.set(s.shipment_id, s))
      } else {
        selectableFilteredShipments.forEach((s) => next.delete(s.shipment_id))
      }
      return next
    })
  }

  const handleSelectShipment = (shipment) => {
    if (isStatusUpdateLocked(shipment.status)) return
    setSelectedMap((prev) => {
      const next = new Map(prev)
      if (next.has(shipment.shipment_id)) next.delete(shipment.shipment_id)
      else next.set(shipment.shipment_id, shipment)
      return next
    })
  }

  const handleRevertDelivery = async (shipment) => {
    const blocked = revertDeliveryDisabledReason(shipment, user)
    if (blocked) {
      Alert.error(blocked)
      return
    }
    const confirmed = await Alert.confirm(
      `Revert delivery for ${shipment.shipment_id}? It will move back to Received, payment will become To-Pay, and collected amount will be reversed.`,
      "Revert Delivery",
    )
    if (!confirmed) return
    try {
      setRevertingId(shipment.shipment_id)
      await shipmentService.revertDelivery(shipment.shipment_id)
      Alert.success("Delivery reverted to Received")
      await fetchData()
    } catch (err) {
      Alert.error(err.response?.data?.error || "Failed to revert delivery")
    } finally {
      setRevertingId("")
    }
  }

  const previewOf = (file, setter) => {
    if (!file) {
      setter(null)
      return
    }
    const reader = new FileReader()
    reader.onloadend = () => setter(reader.result)
    reader.readAsDataURL(file)
  }

  const resetForm = () => {
    setSelectedMap(new Map())
    setDeliveryReceiver("")
    setDeliveryReceiverContact("")
    setDeliveryReceiverImage(null)
    setImagePreview(null)
    setDeliveryGovIdImage(null)
    setGovIdPreview(null)
    setPaymentAmount("")
    setPendingDeliverAfterPay(null)
    setUnloadingCharges("")
  }

  const runStatusUpdate = async (shipmentIds) => {
    const response = await shipmentService.bulkUpdateStatus(
      shipmentIds,
      selectedStatus,
      remarks,
      deliveryReceiver,
      deliveryReceiverImage,
      deliveryReceiverContact,
      showUnloadingInput ? unloadingCharges : null,
      showLoadingInput ? loadingCharges : null,
      deliveryGovIdImage,
      deliveryVehicleNo,
      lastMileHours,
    )
    if (isOtpRequiredResponse(response.data)) {
      setOtpPending(normalizeOtpPending(response.data))
      if (response.data.updated_count > 0) {
        Alert.success(
          `Updated ${response.data.updated_count} shipment(s). Verify OTP to deliver the rest.`,
        )
      } else {
        Alert.success(response.data.message || "OTP sent on WhatsApp")
      }
      return
    }
    if (response.data?.success) {
      const failed = response.data.failed_shipments || []
      if (failed.length > 0 && response.data.updated_count === 0) {
        Alert.error(failed[0]?.reason || "Failed to update shipments")
      } else {
        const ewayNote = (response.data.eway_bill_warnings || []).filter(Boolean).join(" ")
        Alert.success(
          `Successfully updated ${response.data.updated_count} shipment(s) to ${selectedStatus}${ewayNote ? `. ${ewayNote}` : ""}`,
        )
        if (selectedStatus === "Delivered") {
          await printShipmentReceipts(shipmentIds, user)
        }
      }
      resetForm()
      await fetchData()
    } else {
      Alert.error(response.data?.error || "Failed to update shipments")
    }
  }

  const collectDeliveryPayment = async (shipment) => {
    const balance = parseFloat(shipment.balance_amount || 0)
    const amount = parseFloat(paymentAmount || 0)
    if (Number.isNaN(amount) || amount <= 0) {
      throw new Error("Enter a valid payment amount")
    }
    if (amount > balance + 0.001) {
      throw new Error(`Payment cannot exceed balance (₹${balance.toFixed(2)})`)
    }
    if (amount + 0.001 < balance) {
      throw new Error(`Pay the full balance (₹${balance.toFixed(2)}) to mark as Delivered`)
    }

    const payload = new FormData()
    payload.append("payment_amount", String(amount))
    payload.append("payment_mode", paymentMode)
    payload.append("remarks", `${shipment.shipment_id} has been delivered`)

    const response = await paymentService.addPayment(shipment.shipment_id, payload)
    if (response.data?.requires_online_payment && response.data?.payment_intent) {
      setPaymentIntent(response.data.payment_intent)
      setPendingDeliverAfterPay([shipment.shipment_id])
      setShowPaymentQr(true)
      return { deferred: true }
    }
    return { deferred: false }
  }

  const handleBulkStatusUpdate = async () => {
    const shipmentIds = selectedList.map((s) => s.shipment_id)
    if (shipmentIds.length === 0) {
      Alert.error("Please select at least one shipment that is not already Delivered")
      return
    }
    if (!selectedStatus) {
      Alert.error("Please select a status")
      return
    }
    if (selectedStatus === "Delivered" && deliveryBlocked) {
      Alert.error("Delivery is not allowed for your assigned hub")
      return
    }
    const proofsRequired = user?.delivery_details_required !== false
    if (selectedStatus === "Delivered" && !deliveryBlocked && proofsRequired) {
      if (!String(deliveryReceiver || "").trim()) {
        Alert.error("Receiver name is required")
        return
      }
      if (!String(deliveryReceiverContact || "").trim()) {
        Alert.error("Receiver contact is required")
        return
      }
      if (!deliveryReceiverImage) {
        Alert.error("Delivery photo is required")
        return
      }
      if (!deliveryGovIdImage) {
        Alert.error("Government ID proof is required")
        return
      }
    }
    if (!statusOptions.some((o) => o.value === selectedStatus)) {
      Alert.error("Selected status is not allowed for the chosen shipments")
      return
    }
    if (selectedStatus === "Delivered" && unpaidSelected.length > 1) {
      Alert.error(
        "Multiple unpaid shipments selected. Select one at a time to collect delivery payment.",
      )
      return
    }

    try {
      setUpdating(true)
      if (needsDeliveryPayment && unpaidSelected.length === 1) {
        const result = await collectDeliveryPayment(unpaidSelected[0])
        if (result.deferred) return
      }
      await runStatusUpdate(shipmentIds)
    } catch (error) {
      const message = error.response?.data?.error || error.message || "Failed to update shipments"
      if (message !== "Payment cancelled") Alert.error(message)
    } finally {
      setUpdating(false)
    }
  }

  const handleOnlinePaymentSuccess = async () => {
    setShowPaymentQr(false)
    setPaymentIntent(null)
    const ids = pendingDeliverAfterPay
    setPendingDeliverAfterPay(null)
    try {
      setUpdating(true)
      if (ids?.length) {
        await runStatusUpdate(ids)
      } else {
        Alert.success("Payment received")
        await fetchData()
      }
    } catch (error) {
      Alert.error(error.response?.data?.error || "Payment received but status update failed")
      await fetchData()
    } finally {
      setUpdating(false)
    }
  }

  const handleOnlinePaymentCancel = () => {
    setShowPaymentQr(false)
    setPaymentIntent(null)
    setPendingDeliverAfterPay(null)
    setUpdating(false)
  }

  const getShipmentStatusColor = (status) => {
    const statusMap = {
      booked: "bg-blue-100 text-blue-800",
      dispatched: "bg-purple-100 text-purple-800",
      received: "bg-yellow-100 text-yellow-800",
      delivered: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      returned: "bg-orange-100 text-orange-800",
    }
    return statusMap[status?.toLowerCase()] || "bg-gray-100 text-gray-800"
  }

  if (loading && shipments.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading Update Status..." />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className={isMobileLayout ? "pb-24 min-h-0" : ""}>
        {isMobileLayout ? (
          <div className="sticky top-0 z-30 bg-white shrink-0 mb-2 pb-2 border-b border-gray-100">
            <h1 className="flex items-center gap-1.5 text-sm font-bold text-secondary truncate min-w-0">
              <Package className="w-4 h-4 text-buttonhoverTxt shrink-0" />
              <span className="truncate">{t("sidebar.updateStatus", "Update Status")}</span>
            </h1>
            <p className="text-[10px] text-gray-500 mt-1">
              ML sheet shipments assigned to you
            </p>
          </div>
        ) : (
          <div className="p-4 sm:p-6 sm:px-0 mb-4 sm:py-2 sm:mb-2">
            <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2">
              <Package className="w-6 h-6 text-buttonhoverTxt" />
              {t("sidebar.updateStatus", "Update Status")}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {user?.role === "Agent"
                ? "Shipments from Master Loading Sheets assigned to you only."
                : "Shipments linked to Master Loading Sheets you can manage."}
            </p>
          </div>
        )}

        {deliveryBlocked && (
          <div className="mb-3 sm:mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs sm:text-sm">
            Delivery is not enabled for your location hub. You can mark shipments as Received,
            but Delivered is disabled.
          </div>
        )}

        <div className="bg-white rounded-xl p-3 sm:p-4 mb-3 sm:mb-6 shadow-sm md:shadow-none">
          <h3 className="hidden md:block text-base sm:text-lg font-semibold text-gray-700 mb-4 sm:mb-6">
            Update Status
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4">
            <div className="min-w-0">
              <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                Select Status <span className="text-red-500">*</span>
              </label>
              <SelectField
                variant="form"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                options={statusOptions}
                className="min-w-0 w-full"
              />
              <p className="text-[9px] sm:text-xs text-gray-500 mt-1 leading-snug">
                Dispatched → Received. Received → Delivered when paid
                {deliveryBlocked ? " (hub delivery off)" : ""}.
              </p>
              {bulkStatusConfig.paymentMessage && (
                <p className="text-[9px] sm:text-xs text-amber-700 mt-1">{bulkStatusConfig.paymentMessage}</p>
              )}
            </div>

            <div className="min-w-0">
              <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                Selected
              </label>
              <div className="flex items-center justify-center px-2 sm:px-4 py-2 sm:py-3 border rounded-xl bg-blue-50/50 min-h-[38px] sm:min-h-[42px]">
                <div className="text-xs sm:text-sm font-bold text-secondary">{selectedList.length}</div>
                <div className="text-[10px] sm:text-sm text-secondary font-medium ml-1 truncate">
                  / {selectableTotal || selectableFilteredShipments.length} upd
                </div>
              </div>
            </div>

            <div className="col-span-2 md:col-span-1 min-w-0">
              <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                Remarks
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Optional remarks..."
                className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 resize-none md:border-2"
                rows={isMobileLayout ? 2 : 1}
              />
            </div>
          </div>

          {(showUnloadingInput || showLoadingInput) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4 p-2 sm:p-4 bg-blue-50 rounded-xl border border-blue-200">
              <div className="sm:col-span-3">
                <h4 className="text-[10px] sm:text-sm font-semibold text-blue-800">
                  Labour charges
                </h4>
              </div>
              {showLoadingInput && (
                <div className="min-w-0">
                  <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                    Loading charge
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={loadingCharges}
                    onChange={(e) => setLoadingCharges(e.target.value)}
                    placeholder="₹ loading"
                    className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                  />
                </div>
              )}
              {showUnloadingInput && (
              <div className="min-w-0">
                <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Unloading charge
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={unloadingCharges}
                  onChange={(e) => setUnloadingCharges(e.target.value)}
                  placeholder="₹ unloading"
                  className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                />
              </div>
              )}
            </div>
          )}

          {selectedStatus === "Delivered" && !deliveryBlocked && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4 p-2 sm:p-4 bg-orange-50 rounded-xl border border-orange-200">
              <div className="sm:col-span-3">
                <h4 className="text-[10px] sm:text-sm font-semibold text-orange-800">
                  Delivery proof {user?.delivery_details_required === false ? "(optional)" : "(required)"}
                </h4>
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Last-mile vehicle
                </label>
                <input
                  type="text"
                  value={deliveryVehicleNo}
                  onChange={(e) => setDeliveryVehicleNo(e.target.value.toUpperCase())}
                  placeholder="Needed when an e-Way Bill is on the GC"
                  className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                  maxLength={20}
                />
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  E-Way close window
                </label>
                <select
                  value={lastMileHours}
                  onChange={(e) => setLastMileHours(e.target.value)}
                  className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                >
                  <option value="1">1 hour</option>
                  <option value="2">2 hours</option>
                  <option value="3">3 hours</option>
                </select>
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Receiver Name
                </label>
                <input
                  ref={deliveryNameRef}
                  type="text"
                  value={deliveryReceiver}
                  onChange={(e) => setDeliveryReceiver(e.target.value)}
                  onKeyDown={onEnter(() => deliveryContactRef.current?.focus())}
                  placeholder="Receiver name"
                  className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                  maxLength={250}
                />
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Receiver Contact
                </label>
                <input
                  ref={deliveryContactRef}
                  type="text"
                  value={deliveryReceiverContact}
                  onChange={(e) =>
                    setDeliveryReceiverContact(e.target.value.replace(/[^\d+\-\s]/g, ""))
                  }
                  onKeyDown={onEnter(goToAmountOrSubmit)}
                  placeholder="Contact number"
                  className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                  maxLength={30}
                />
              </div>
              <div className="sm:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CapturePhotoField
                label="Delivery photo"
                required={user?.delivery_details_required !== false}
                file={deliveryReceiverImage}
                preview={imagePreview}
                disabled={updating}
                onPick={(file) => {
                  setDeliveryReceiverImage(file)
                  previewOf(file, setImagePreview)
                }}
                onClear={() => {
                  setDeliveryReceiverImage(null)
                  setImagePreview(null)
                }}
              />
              <CapturePhotoField
                label="Government ID proof"
                required={user?.delivery_details_required !== false}
                file={deliveryGovIdImage}
                preview={govIdPreview}
                disabled={updating}
                onPick={(file) => {
                  setDeliveryGovIdImage(file)
                  previewOf(file, setGovIdPreview)
                }}
                onClear={() => {
                  setDeliveryGovIdImage(null)
                  setGovIdPreview(null)
                }}
              />
              </div>
            </div>
          )}

          {selectedStatus === "Delivered" && !deliveryBlocked && creditSelected.length > 0 && (
            <div className="mb-3 sm:mb-4 p-3 sm:p-4 rounded-xl border border-sky-200 bg-sky-50 text-sky-950">
              <h4 className="text-xs sm:text-sm font-semibold">This is a credit client</h4>
              <p className="text-xs sm:text-sm mt-1">
                {creditSelected
                  .map((s) => {
                    const who = s.shipment_client_company || s.shipment_client_name
                    return who ? `${s.shipment_id} (${who})` : s.shipment_id
                  })
                  .join(", ")}
                . Do not collect the amount or payment mode. The freight stays on the shipment client.
              </p>
            </div>
          )}

          {needsDeliveryPayment && !deliveryBlocked && (
            <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-amber-50 rounded-xl border border-amber-200">
              <h4 className="text-xs sm:text-sm font-semibold text-amber-900 mb-2">
                Collect payment on delivery
              </h4>
              {unpaidSelected.length > 1 ? (
                <p className="text-xs sm:text-sm text-red-600">
                  Select only one unpaid shipment to collect Cash or UPI, then mark Delivered.
                </p>
              ) : (
                <>
                  <p className="text-xs sm:text-sm text-gray-700 mb-3">
                    <span className="font-semibold">{unpaidSelected[0].shipment_id}</span> balance{" "}
                    <span className="font-semibold">
                      ₹{parseFloat(unpaidSelected[0].balance_amount || 0).toFixed(2)}
                    </span>
                    . Pay full balance to mark Delivered &amp; Paid.
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <div>
                      <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1">
                        Amount
                      </label>
                      <input
                        ref={paymentAmountRef}
                        type="number"
                        min="0"
                        step="0.01"
                        value={paymentAmount}
                        readOnly
                        tabIndex={-1}
                        aria-readonly="true"
                        title="Full balance is locked for delivery payment"
                        className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-xl bg-gray-100 text-gray-800 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1">
                        Method
                      </label>
                      <select
                        ref={paymentModeRef}
                        value={paymentMode}
                        onChange={(e) => setPaymentMode(e.target.value)}
                        onKeyDown={onEnter(focusVisibleSubmit)}
                        className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-xl"
                      >
                        <option value="upi">UPI (QR)</option>
                        <option value="cash">Cash</option>
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <Button
            variant="primary"
            type="button"
            data-agent-status-submit="true"
            onClick={handleBulkStatusUpdate}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !updating) {
                event.preventDefault()
                handleBulkStatusUpdate()
              }
            }}
            disabled={
              updating ||
              selectedList.length === 0 ||
              (needsDeliveryPayment && unpaidSelected.length > 1)
            }
            loading={updating}
            className="hidden md:inline-flex"
          >
            {updating
              ? "Updating..."
              : needsDeliveryPayment
                ? `Pay & Update ${selectedList.length} Shipment(s)`
                : `Update ${selectedList.length} Shipment(s)`}
          </Button>
        </div>

        <div className={`bg-white rounded-xl shadow-md overflow-hidden max-w-full ${loading ? "opacity-70" : ""}`}>
          <div className="px-3 sm:px-6 py-2 sm:py-4 border-b border-gray-200 space-y-2 sm:space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={handleSelectAll}
                  disabled={selectableFilteredShipments.length === 0}
                  className="w-4 h-4 sm:w-5 sm:h-5 rounded border-2 border-gray-400 cursor-pointer accent-blue-500 disabled:opacity-40"
                />
                <h3 className="text-xs sm:text-lg font-semibold text-gray-700 mb-0">
                  Shipments (
                  {totalRecords === 0
                    ? 0
                    : `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, totalRecords)} of ${totalRecords}`}
                  )
                </h3>
              </div>
              <div className="flex-1 min-w-0">
                <SearchSubmitInput
                  value={searchQuery}
                  onSearch={setSearchQuery}
                  placeholder="Exact match: ID, name, contact, ML sheet..."
                  ariaLabel="Search shipments"
                  inputClassName="w-full px-3 py-1.5 sm:py-2.5 border border-gray-300 rounded-xl text-[11px] sm:text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-orange-400"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <span className="text-[10px] sm:text-xs font-semibold text-gray-600 mr-0.5">
                Status:
              </span>
              {FILTER_OPTIONS.map((status) => {
                const active = statusFilters.includes(status)
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => toggleStatusFilter(status)}
                    className={`px-2.5 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-semibold rounded-xl border transition-colors ${
                      active
                        ? "bg-secondary text-white border-secondary"
                        : "bg-white text-gray-600 border-gray-300 hover:border-secondary"
                    }`}
                  >
                    {status}
                  </button>
                )
              })}
              <span className="hidden sm:inline text-xs text-gray-500 ml-1">
                Default: Dispatched + Received
              </span>
            </div>
          </div>

          {filteredShipments.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-gray-500 text-sm sm:text-lg">
                {totalRecords === 0 && !searchQuery.trim() && statusFilters.length >= FILTER_OPTIONS.length
                  ? "No shipments on ML sheets assigned to you"
                  : "No shipments match the selected filters"}
              </p>
            </div>
          ) : (
            <>
              <div className="hidden xl:block w-full overflow-x-auto">
                <table className="w-full min-w-[1280px] border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left w-12">
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          onChange={handleSelectAll}
                          disabled={selectableFilteredShipments.length === 0}
                          className="w-4 h-4 rounded border border-gray-400 cursor-pointer accent-blue-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Shipment ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        ML Sheet
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Sender
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Sender Branch
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Receiver
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Receiver Branch
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Article
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Amount / Balance
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredShipments.map((shipment, index) => {
                      const locked = isStatusUpdateLocked(shipment.status)
                      const canRevert = canRevertDelivery(shipment, user)
                      const revertBlockedReason = revertDeliveryDisabledReason(shipment, user)
                      return (
                        <tr
                          key={shipment.id || shipment.shipment_id}
                          className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"} ${
                            locked ? "bg-green-50/40" : "hover:bg-gray-50"
                          } ${
                            selectedShipments.has(shipment.shipment_id)
                              ? "ring-1 ring-inset ring-orange-300"
                              : ""
                          }`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedShipments.has(shipment.shipment_id)}
                              onChange={() => handleSelectShipment(shipment)}
                              disabled={locked}
                              title={locked ? "Already delivered — locked" : undefined}
                              className="w-4 h-4 rounded border border-gray-400 accent-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                              className="font-semibold text-secondary hover:text-orange-600 hover:underline"
                            >
                              {shipment.shipment_id}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-800">
                            {shipment.ml_sheet_id || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="font-semibold text-gray-900">{shipment.sender_name}</div>
                            <div className="text-xs text-gray-600">{shipment.sender_contact}</div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <SenderBranchValue shipment={shipment} />
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="font-semibold text-gray-900">{shipment.receiver_name}</div>
                            <div className="text-xs text-gray-600">{shipment.receiver_contact}</div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <ReceiverBranchValue shipment={shipment} />
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <ArticleNames shipment={shipment} />
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            <QuantityValue shipment={shipment} />
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            <div className="font-semibold text-gray-900">
                              ₹{parseFloat(shipment.total_amount || 0).toFixed(2)}
                            </div>
                            {isCreditClientShipment(shipment) ? (
                              <div className="text-xs text-sky-800 font-semibold">
                                On shipment client
                              </div>
                            ) : (
                            <div
                              className={
                                parseFloat(shipment.balance_amount || 0) > 0
                                  ? "text-xs text-amber-700 font-semibold"
                                  : "text-xs text-green-700"
                              }
                            >
                              Bal: ₹{parseFloat(shipment.balance_amount || 0).toFixed(2)}
                            </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase ${getShipmentStatusColor(
                                shipment.status,
                              )}`}
                            >
                              {shipment.status}
                            </span>
                            <div className="text-xs text-gray-600 mt-1">
                              {isCreditClientShipment(shipment) ? "Credit client" : shipment.mode_of_payment}
                            </div>
                            {locked && (
                              <div className="text-xs text-red-600 font-semibold mt-0.5">Locked</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <div className="flex items-center justify-center gap-2">
                              {locked && (
                                canRevert ? (
                                  <button
                                    type="button"
                                    onClick={() => handleRevertDelivery(shipment)}
                                    disabled={revertingId === shipment.shipment_id}
                                    title="Revert delivery to Received"
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-amber-700 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    <Undo2 className={`w-4 h-4 ${revertingId === shipment.shipment_id ? "animate-spin" : ""}`} />
                                  </button>
                                ) : (
                                  <span
                                    title={revertBlockedReason || "Revert is not available"}
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 bg-gray-100 cursor-not-allowed"
                                    aria-disabled="true"
                                  >
                                    <Ban className="w-4 h-4" />
                                  </span>
                                )
                              )}
                              <button
                                type="button"
                                onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                                className="text-orange-600 hover:text-orange-700 font-semibold text-sm hover:underline"
                              >
                                View →
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className="xl:hidden space-y-2 p-2 sm:p-4">
                {filteredShipments.map((shipment) => {
                  const isSelected = selectedShipments.has(shipment.shipment_id)
                  const locked = isStatusUpdateLocked(shipment.status)
                  const canRevert = canRevertDelivery(shipment, user)
                  const revertBlockedReason = revertDeliveryDisabledReason(shipment, user)
                  return (
                    <div
                      key={shipment.id || shipment.shipment_id}
                      className={`bg-white rounded-xl shadow-sm border p-2.5 sm:p-4 border-l-4 transition-colors ${
                        locked
                          ? "border-gray-200 border-l-green-500 bg-green-50/30"
                          : isSelected
                            ? "border-orange-400 border-l-orange-500 bg-orange-50/40"
                            : "border-gray-100 border-l-orange-500"
                      }`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectShipment(shipment)}
                          disabled={locked}
                          className="w-4 h-4 rounded border-2 border-gray-400 accent-blue-500 mt-0.5 shrink-0 disabled:opacity-50"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-start justify-between gap-1">
                            <button
                              type="button"
                              onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                              className="text-orange-600 font-bold text-xs sm:text-sm break-all hover:underline text-left"
                            >
                              {shipment.shipment_id}
                            </button>
                            <span
                              className={`px-1.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase shrink-0 ${getShipmentStatusColor(
                                shipment.status,
                              )}`}
                            >
                              {shipment.status}
                            </span>
                          </div>
                          <p className="text-[10px] text-gray-600 mt-0.5">
                            ML: {shipment.ml_sheet_id || "—"}
                          </p>
                          <p className="text-xs sm:text-sm font-bold text-gray-900 mt-1">
                            ₹{parseFloat(shipment.total_amount || 0).toFixed(2)}
                            {isCreditClientShipment(shipment) ? (
                              <span className="ml-2 text-sky-800 font-semibold">Credit client</span>
                            ) : parseFloat(shipment.balance_amount || 0) > 0 ? (
                              <span className="ml-2 text-amber-700 font-semibold">
                                Bal ₹{parseFloat(shipment.balance_amount || 0).toFixed(2)}
                              </span>
                            ) : null}
                          </p>
                          {locked && (
                            <p className="text-[10px] text-red-600 font-semibold mt-1">
                              Already delivered — locked
                            </p>
                          )}
                          {locked && (
                            canRevert ? (
                              <button
                                type="button"
                                onClick={() => handleRevertDelivery(shipment)}
                                disabled={revertingId === shipment.shipment_id}
                                title="Revert delivery to Received"
                                className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 disabled:opacity-40"
                              >
                                <Undo2 className={`w-3.5 h-3.5 ${revertingId === shipment.shipment_id ? "animate-spin" : ""}`} />
                                Revert delivery
                              </button>
                            ) : (
                              <span
                                title={revertBlockedReason || "Revert is not available"}
                                className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-gray-400 cursor-not-allowed"
                                aria-disabled="true"
                              >
                                <Ban className="w-3.5 h-3.5" />
                                Revert disabled
                              </span>
                            )
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[10px] sm:text-sm">
                        <div className="min-w-0">
                          <p className="text-gray-600 font-medium">Sender</p>
                          <p className="font-semibold text-gray-900 break-words leading-tight">
                            {shipment.sender_name}
                          </p>
                          <p className="text-gray-600">{shipment.sender_contact}</p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-gray-600 font-medium">Receiver</p>
                          <p className="font-semibold text-gray-900 break-words leading-tight">
                            {shipment.receiver_name}
                          </p>
                          <p className="text-gray-600">{shipment.receiver_contact}</p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-gray-600 font-medium">Sender Branch</p>
                          <p className="font-semibold text-gray-900 break-words leading-tight">
                            <SenderBranchValue shipment={shipment} />
                          </p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-gray-600 font-medium">Receiver Branch</p>
                          <p className="font-semibold text-gray-900 break-words leading-tight">
                            <ReceiverBranchValue shipment={shipment} />
                          </p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-gray-600 font-medium">Article</p>
                          <p className="font-semibold text-gray-900 break-words leading-tight">
                            <ArticleNames shipment={shipment} />
                          </p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-gray-600 font-medium">Quantity</p>
                          <p className="font-semibold text-gray-900">
                            <QuantityValue shipment={shipment} />
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              {totalRecords > 0 && (
                <ListPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  loading={loading && currentPage > 1}
                  onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
                />
              )}
            </>
          )}
        </div>
      </div>

      {isMobileLayout && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] md:hidden">
          <Button
            variant="primary"
            type="button"
            icon={RefreshCw}
            data-agent-status-submit="true"
            onClick={handleBulkStatusUpdate}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !updating) {
                event.preventDefault()
                handleBulkStatusUpdate()
              }
            }}
            disabled={
              updating ||
              selectedList.length === 0 ||
              (needsDeliveryPayment && unpaidSelected.length > 1)
            }
            loading={updating}
            fullWidth
            className="w-full justify-center"
          >
            {updating
              ? "Updating..."
              : needsDeliveryPayment
                ? `Pay & Update ${selectedList.length}`
                : `Update ${selectedList.length} Shipment(s)`}
          </Button>
        </div>
      )}

      <PaymentQrModal
        open={showPaymentQr}
        intent={paymentIntent}
        title="Scan UPI QR to Pay"
        onSuccess={handleOnlinePaymentSuccess}
        onCancel={handleOnlinePaymentCancel}
      />
      <DeliveryOtpModal
        open={otpPending.length > 0}
        pending={otpPending}
        onClose={() => setOtpPending([])}
        onVerified={async () => {
          const deliveredIds = otpPending.map((row) => row.shipment_id).filter(Boolean)
          setOtpPending([])
          resetForm()
          Alert.success("Delivery verified and completed")
          await printShipmentReceipts(deliveredIds, user)
          await fetchData()
        }}
      />
    </DashboardLayout>
  )
}

export default AgentUpdateStatus
